import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { chatService } from "../../services/ChatService";
import { ChatMessage, TicketCategory } from "../../types/chat";
import { socketService } from "../../api/socket";
import { toast } from "react-toastify";

export const useChat = (isGuest: boolean = false) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [chatId, setChatId] = useState<string | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [departmentRoles, setDepartmentRoles] = useState<{
    [key: string]: number;
  }>({});
  const [isSending, setIsSending] = useState(false);
  const [isEndingChat, setIsEndingChat] = useState(false);
  const [departments, setDepartments] = useState<string[]>([]);
  const [ticketdepartments, setTicketDepartments] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [hasOnlineStaff, setHasOnlineStaff] = useState(true);
  const [chatStatus, setChatStatus] = useState<"pending" | "active" | "ended">(
    "pending"
  );
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [guestSubject, setGuestSubject] = useState("");
  const [departmentAvailability, setDepartmentAvailability] = useState<
    Record<string, boolean>
  >({});
  const [attachmentPreview, setAttachmentPreview] = useState<{
    file: File;
    preview: string;
  } | null>(null);
  const [ticketForm, setTicketForm] = useState({
    subject: "",
    description: "",
    departments: "",
    priority: "medium" as "low" | "medium" | "high" | "urgent",
  });

  const queryClient = useQueryClient();
  const chatHistoryQueryKey = isGuest
    ? ["guest-chat-history", chatId]
    : ["chat-history", chatId];

  useEffect(() => {
    if (isGuest && guestEmail && !socketService.socketInstance) {
      socketService.initializeGuest(guestEmail, {
        onChatMessage: handleNewMessage,
        onChatStatusUpdate: handleStatusUpdate,
        onChatStaffJoined: () => setChatStatus("active"),
        onChatStaffLeft: () => {},
        onChatEnded: () => setChatStatus("ended"),
        onChatTransferred: () => {},
        onNewChat: () => {},
        processNotification: () => {},
        setNotifications: () => {},
        setNewNotification: () => {},
      });
    }
  }, [isGuest, guestEmail]);

  useEffect(() => {
    // A guest has no socket until they submit their email (see effect above),
    // so poll while browsing pre-chat; once a socket exists, push replaces polling.
    const socket = socketService.socketInstance;
    let pollingInterval: NodeJS.Timeout | undefined;

    const updateStaffAvailability = async () => {
      try {
        const response = await chatService.checkStaffAvailability();
        const availabilityMap = response.departments.reduce(
          (acc: Record<string, boolean>, dept: string) => {
            acc[dept] = response.available_staff.some(
              (staff) => staff.role === dept && staff.available_for_chat
            );
            return acc;
          },
          {}
        );

        // Check if any department has available staff
        const hasAnyAvailableStaff = Object.values(availabilityMap).some(
          (available) => available
        );
        setHasOnlineStaff(hasAnyAvailableStaff);

        setDepartmentAvailability(availabilityMap);
        setDepartments(response.departments);
        setTicketDepartments(response.departments);

        setDepartmentRoles(response.department_roles);
      } catch (error) {
        console.error("Error updating staff availability:", error);
      }
    };

    if (socket) {
      socket.on("user-status-changed", updateStaffAvailability);
      socket.on("staff.availability.changed", updateStaffAvailability);
      socket.on("staff.status.update", updateStaffAvailability);
    } else if (isGuest) {
      pollingInterval = setInterval(updateStaffAvailability, 10000);
    }

    updateStaffAvailability();

    return () => {
      clearInterval(pollingInterval);
      socket?.off("user-status-changed", updateStaffAvailability);
      socket?.off("staff.availability.changed", updateStaffAvailability);
      socket?.off("staff.status.update", updateStaffAvailability);
    };
  }, [isGuest, guestEmail]);

  useEffect(() => {
    if (chatId && isGuest && guestEmail) {
      setChatStatus("pending");
      setIsEndingChat(false);
      chatService.subscribeToGuestChat(chatId, guestEmail);
    }
  }, [chatId, isGuest, guestEmail]);


  const handleStatusUpdate = (data: {
    status: string;
    chatRoom: any;
    chatId?: string;
    guestEmail?: string;
    isGuestSpecific?: boolean;
  }) => {
    if (isGuest && data.isGuestSpecific) {
      if (data.chatId === chatId && data.guestEmail === guestEmail) {
        if (data.status === "ended") {
          handleChatEnd();
        } else if (data.status === "active") {
          setChatStatus("active");
        }
      }
      return;
    }
    if (data.chatRoom && data.chatRoom.id === chatId) {
      if (data.status === "ended") {
        handleChatEnd();
      } else if (data.status === "active") {
        setChatStatus("active");
      }
    }
  };

  const handleFileUpload = async (files: FileList) => {
    if (!chatId || !files.length) return;

    const file = files[0];
    const MAX_FILE_SIZE = 2 * 1024 * 1024;

    if (file.size > MAX_FILE_SIZE) {
      toast.error("File size too big. Maximum allowed size is 2MB");
      return;
    }

    if (file.type.startsWith("image/")) {
      setAttachmentPreview({
        file,
        preview: URL.createObjectURL(file),
      });
    }
  };

  const handleSendAttachment = async () => {
    if (!attachmentPreview || !chatId) return;

    setIsUploading(true);
    try {
      const fileList = new DataTransfer();
      fileList.items.add(attachmentPreview.file);

      await chatService.uploadAttachment(
        chatId,
        fileList.files,
        isGuest,
        guestEmail
      );

      queryClient.invalidateQueries(chatHistoryQueryKey);
      setAttachmentPreview(null);
    } catch (error) {
      console.error("Failed to upload file:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleNewMessage = (incoming: any) => {
    const message: ChatMessage | null = incoming?.content
      ? incoming
      : incoming?.message?.content
      ? incoming.message
      : null;

    if (!message || !message.content) {
      queryClient.invalidateQueries(chatHistoryQueryKey);
      return;
    }

    if (typeof message.id !== "number") {
      queryClient.invalidateQueries(chatHistoryQueryKey);
      return;
    }

    queryClient.setQueryData(chatHistoryQueryKey, (oldData: any) => {
      if (!oldData) {
        return { messages: [message] };
      }
      return {
        ...oldData,
        messages: [...oldData.messages, message],
      };
    });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !chatId || isSending) return;
    
    setIsSending(true);
    try {
      const messageContent = {
        message_type: "text" as const,
        body: newMessage.trim(),
        sender_type: (isGuest ? "guest" : "user") as "guest" | "user",
        sender_role: isGuest ? "guest" : "User",
        metadata: {
          read: false,
          read_at: null,
          sent_at: new Date().toISOString(),
          reply_to: replyTo
            ? {
                id: replyTo.id,
                body: replyTo.content.body,
                sender_name:
                  replyTo.content.metadata?.guest_name ||
                  replyTo.sender?.name ||
                  "message",
              }
            : undefined,
        },
      };

      if (isGuest) {
        await chatService.sendGuestMessage(chatId, messageContent, guestEmail);
      } else {
        await chatService.sendMessage(chatId, messageContent);
      }

      setNewMessage("");
      setReplyTo(null);
      queryClient.invalidateQueries(chatHistoryQueryKey);
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setIsSending(false);
    }
  };
  

  const handleChatEnd = async () => {
    if (!chatId) return;
    const endedChatId = chatId;

    try {
      if (isGuest) {
        await chatService.endGuestChat(chatId, guestEmail);
      } else {
        await chatService.endChat(chatId, guestEmail);
      }
    } catch (error) {
      console.error("Failed to end chat:", error);
      toast.error("Failed to end chat. Please try again.");
      return;
    }

    setChatStatus("ended");
    queryClient.removeQueries(
      isGuest
        ? ["guest-chat-history", endedChatId]
        : ["chat-history", endedChatId]
    );
    setChatId(null);
  };

  return {
    isOpen,
    setIsOpen,
    isMinimized,
    setIsMinimized,
    newMessage,
    setNewMessage,
    replyTo,
    setReplyTo,
    chatId,
    setChatId,
    selectedDepartment,
    setSelectedDepartment,
    isSending,
    departments,
    isUploading,
    hasOnlineStaff,
    guestName,
    setGuestName,
    guestEmail,
    setGuestEmail,
    guestPhone,
    setGuestPhone,
    guestSubject,
    setGuestSubject,
    ticketForm,
    setTicketForm,
    handleNewMessage,
    handleStatusUpdate,
    handleFileUpload,
    handleChatEnd,
    ticketdepartments,
    attachmentPreview,
    setAttachmentPreview,
    handleSendAttachment,
    departmentAvailability,
    departmentRoles,
    chatStatus,
    setChatStatus,
    handleSendMessage,
  };
};
